/**
 * Utility functions for tier management and Free tier detection
 */


/**
 * Check if the user is on the Advanced tier (highest tier)
 * @param assignedTier - The tier object from the user profile
 * @returns boolean - true if user is on Advanced tier, false otherwise
 */
export const isAdvancedTier = (assignedTier: any): boolean => {
    if (!assignedTier || !assignedTier.name) {
        return false;
    }
    
    // Check if tier name contains "Advanced" (case-insensitive, with whitespace handling)
    const tierName = assignedTier.name.trim().toLowerCase();
    
    // Check for various possible Advanced tier names
    return tierName === 'sbr2th advanced' || 
           tierName === 'advanced' || 
           tierName.includes('advanced');
};

/**
 * Check if the user should see upgrade buttons (hide for Advanced tier users)
 * @param assignedTier - The tier object from the user profile
 * @returns boolean - true if user should see upgrade buttons, false otherwise
 */
export const shouldShowUpgradeButton = (assignedTier: any): boolean => {
    // If no tier data is available yet, don't show upgrade button (wait for data to load)
    if (!assignedTier || !assignedTier.name) {
        return false;
    }
    
    const isAdvanced = isAdvancedTier(assignedTier);
    return !isAdvanced;
};

/**
 * Get tier limitation information for Free tier users
 * @param assignedTier - The tier object from the user profile
 * @returns object with tier limitation details or null for premium users
 */
export const getTierLimitations = (assignedTier: any) => {
    if (!assignedTier) {
        return null;
    }
    
    return {
        maxDownloads: assignedTier.no_of_downloads ?? 0,
        maxProfileViews: assignedTier.no_of_profile_views ?? 0,
        maxJobCreations: assignedTier.no_of_job_creation ?? 0,
        maxCandidatesVisible: assignedTier.no_of_profile_views ?? 0
    };
};

/**
 * Get upgrade message for Free tier users
 * @param assignedTier - The tier object from the user profile
 * @param context - Context for the upgrade message (e.g., 'candidates', 'downloads', 'profile_views')
 * @returns string - Upgrade message or empty string for premium users
 */
export const getUpgradeMessage = (assignedTier: any, context: string = 'general'): string => {
    if (!isFreeTierByProperties(assignedTier)) {
        return '';
    }
    
    const messages = {
        candidates: 'Upgrade to view all qualified candidates and unlock full candidate details.',
        downloads: 'Upgrade to download candidate profiles and resumes.',
        profile_views: 'Upgrade to view unlimited candidate profiles.',
        job_creation: 'Upgrade to create unlimited job postings.',
        general: 'Upgrade to unlock premium features and remove limitations.',
        hover: 'Upgrade to unlock full candidate details.'
    };
    
    return messages[context as keyof typeof messages] || messages.general;
};

/**
 * Check if candidate data should be masked based on mask boolean from tier assignment
 * @param candidate - The candidate object from the API (optional, kept for backward compatibility)
 * @param assignedTier - The tier assignment object from user profile (contains mask field)
 * @returns boolean - true if candidate should be masked, false otherwise
 */
export const shouldMaskCandidate = (candidate: any, assignedTier?: any): boolean => {
    // Check assignedTier.mask first (primary source)
    if (assignedTier && assignedTier.mask !== undefined) {
        return assignedTier.mask === true;
    }
    
    // Fallback to candidate.mask for backward compatibility
    if (candidate && candidate.mask !== undefined) {
        return candidate.mask === true;
    }

    return false;
};

/**
 * Check if downloads should be blocked based on mask data from tier assignment
 * @param candidates - Array of candidate objects (optional, kept for backward compatibility)
 * @param assignedTier - The tier assignment object from user profile (contains mask field)
 * @returns boolean - true if downloads should be blocked, false otherwise
 */
export const shouldBlockDownloads = (candidates: any[] = [], assignedTier?: any): boolean => {
    // Check assignedTier.mask first (primary source)
    if (assignedTier && assignedTier.mask !== undefined) {
        return assignedTier.mask === true;
    }
    
    // Fallback: Block downloads if any candidate is masked (backward compatibility)
    if (candidates && candidates.length > 0) {
        return candidates.some(candidate => shouldMaskCandidate(candidate, assignedTier));
    }
    
    return false;
};

/**
 * Check if upgrade prompt should be shown based on mask data and tier limitations
 * @param candidates - Array of candidate objects
 * @param tierLimitation - Tier limitation object from API
 * @returns boolean - true if upgrade prompt should be shown, false otherwise
 */
export const shouldShowUpgradePrompt = (candidates: any[], tierLimitation: any, assignedTier: any): boolean => {
    if (!candidates || candidates.length === 0) {
        return false;
    }

    // Only show this footer for demo/free accounts
    const isDemo = !!(assignedTier?.mask === true || tierLimitation?.is_free_tier);

    const viewLimit = assignedTier?.no_of_profile_views ?? 50;
    return isDemo && candidates.length >= viewLimit;
};

/**
 * Check if a tier is free based on tier properties (alternative to name-based check)
 * @param assignedTier - The tier object from the user profile
 * @returns boolean - true if tier is free, false otherwise
 */
export const isFreeTierByProperties = (assignedTier: any): boolean => {
    if (!assignedTier) {
        return false;
    }
    // Treat only mask=true as demo/free indicator
    return assignedTier.mask === true;
};

/**
 * Common upgrade handler that navigates to contact us to upgrade page
 * @param navigate - React Router navigate function
 * @param contactRoute - Route to contact us page (defaults to ROUTES.CONTACT_US_TO_UPGRADE_ROUTE)
 */
export const handleUpgrade = (navigate: any, contactRoute: string = '/contact-us-to-upgrade') => {
    navigate(contactRoute);
};
